import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { withRouter } from 'react-router5';
import { createSelector } from 'reselect';

import { useGenericModal } from '@neptune/shared/common-business-logic';
import { handleError } from '@neptune/shared/common-domain';
import { Organization } from '@neptune/shared/core-organizations-domain';
import { Layout } from '@neptune/shared/venus-ui';
import { cancelSubscriptionAndLogout } from '@neptune/user-management-business-logic';
import { CANCEL_SUBSCRIPTION_ERROR_MODAL } from '@neptune/user-management-domain';
import {
  CancelSubscriptionButton,
  CancelSubscriptionErrorModal,
  CancelSubscriptionModal,
} from '@neptune/user-management-ui';

import { getOrganizationsList } from 'state/selectors';
import { openConfirmationModal } from 'state/ui/global/confirmation-modal/actions';
import { showGenericModal } from 'state/ui/modals/generic/actions';
import { getCurrentUser } from 'state/users/current-user/selectors';

const getUserOrganizationId = createSelector(
  [getCurrentUser, getOrganizationsList],
  (currentUser, organizations: Organization[]) => {
    const userOrganization = organizations.find(
      (organization) => organization.name === currentUser?.username,
    );
    return userOrganization?.id;
  },
);

export const CancelSubscriptionButtonContainer: React.FC = withRouter(({ router }) => {
  const dispatch = useDispatch();
  const organizationId = useSelector(getUserOrganizationId);

  const { isOpen: isCancelSubscriptionErrorModalOpen, close: closeCancelSubscriptionErrorModal } =
    useGenericModal(CANCEL_SUBSCRIPTION_ERROR_MODAL);

  const onRequestCancelSubscription = React.useCallback(() => {
    dispatch(openConfirmationModal('cancelSubscription'));
  }, [dispatch]);

  const onExecuteCancelSubscription = React.useCallback(async () => {
    if (!organizationId) {
      return;
    }

    try {
      await cancelSubscriptionAndLogout(router, organizationId);
    } catch (e) {
      handleError(400, e, () => {
        dispatch(showGenericModal(CANCEL_SUBSCRIPTION_ERROR_MODAL));
      });
    }
  }, [dispatch, router, organizationId]);

  if (!organizationId) {
    return null;
  }

  return (
    <Layout.Element>
      <CancelSubscriptionButton onClick={onRequestCancelSubscription} />
      <CancelSubscriptionModal onCancelSubscription={onExecuteCancelSubscription} />
      <CancelSubscriptionErrorModal
        isOpen={isCancelSubscriptionErrorModalOpen}
        close={closeCancelSubscriptionErrorModal}
      />
    </Layout.Element>
  );
});
